import { useEffect, useState } from 'react'
import { authApi } from '../api/auth.api'
import { tenantApi } from '../api/tenant.api'
import { MOCK_TOKENS, makeMockTenant, makeMockUser, resolveMockRole } from '../data/mock-user'
import { useAuthStore } from '../stores/auth.store'
import { useImpersonationStore } from '../stores/impersonation.store'
import { buildImpersonatedUser } from '../utils/impersonated-user'
import { captureHashTokenOnce } from '../utils/hash-token'
import type { UserRole } from '@/lib/rbac'

export type SessionStatus = 'pending' | 'ready'

/**
 * Work decided once, before first paint, from the URL and the rehydrated
 * store. `null` means there is nothing to do (the common case).
 */
type BootstrapTask =
  | { kind: 'mock'; role: UserRole }
  | { kind: 'handoff'; token: string }
  | { kind: 'restore' }
  | null

/**
 * One-time session bootstrap, run above the router. Handles three cases:
 *
 *  0. **Mock** (dev only) — `VITE_DEV_MOCK_ROLE` is set, so we inject a fake
 *     user/role for that value. A real access token already persisted in
 *     localStorage is preserved so live API calls keep working; we only fall
 *     back to fake tokens when there is no real session (see mock-user.ts).
 *  1. **Handoff** — a JWT arrives in the redirect URL hash (`/#token=<jwt>`).
 *     Validate it via `GET /auth/me`; on success establish the session.
 *  2. **Restore** — a token was persisted to localStorage by a previous
 *     session, but the user profile (never persisted) is gone after a reload.
 *     Re-fetch `GET /auth/me` with the stored token and repopulate the store.
 *
 * Returns `'pending'` only while a fetch is in flight and `'ready'` otherwise
 * — including the no-work case, so normal navigations render with no spinner
 * flash. The router (and its guards, which read `user`/`isAuthenticated`
 * synchronously) mounts only once this is `'ready'`, so the session is always
 * in place before any guard runs.
 */
export function useSessionBootstrap(): SessionStatus {
  // Decide the work exactly once. `captureHashTokenOnce` is StrictMode-safe,
  // and the persist middleware rehydrates synchronously, so the store token
  // is already available here.
  const [task] = useState<BootstrapTask>(() => {
    // Dev-only: VITE_DEV_MOCK_ROLE short-circuits the real flow (see mock-user.ts).
    const mockRole = resolveMockRole()
    if (mockRole) return { kind: 'mock', role: mockRole }
    const hashToken = captureHashTokenOnce()
    if (hashToken) return { kind: 'handoff', token: hashToken }
    const { accessToken, user } = useAuthStore.getState()
    if (accessToken && !user) return { kind: 'restore' }
    return null
  })

  const [status, setStatus] = useState<SessionStatus>(task ? 'pending' : 'ready')

  useEffect(() => {
    if (!task) return
    // Holder object (not a bare `let`) so the flag reads as mutable state
    // across the async closure rather than a statically-known constant.
    const live = { current: true }
    const { setSession, setSessionFromToken, setUser, setTenant, clearSession } =
      useAuthStore.getState()

    void (async () => {
      try {
        if (task.kind === 'mock') {
          // Dev-only: inject a fake user/role synchronously, no backend call.
          // Keep any real persisted token so live API calls still work; only
          // borrow fake tokens when there is no real session to preserve.
          if (live.current) {
            const realToken = useAuthStore.getState().accessToken
            if (realToken) setUser(makeMockUser(task.role))
            else setSession({ user: makeMockUser(task.role), tokens: MOCK_TOKENS })
            setTenant(makeMockTenant(task.role))
          }
        } else if (task.kind === 'handoff') {
          const user = await authApi.me(task.token)
          if (live.current) setSessionFromToken({ user, accessToken: task.token })
        } else {
          // The stored access token is attached by the axios interceptor; an
          // expired token triggers the single-flight refresh transparently.
          // While a super admin is impersonating, the interceptor sends the
          // impersonation token, so `/auth/me` still resolves the super admin
          // identity (attribution) — rebuild the effective account admin so a
          // reload lands back inside the account (AMLIQ-137).
          const user = await authApi.me()
          const session = useImpersonationStore.getState().session
          const detail = session ? await tenantApi.detail(session.tenant.id) : null
          if (live.current) {
            if (session && detail) {
              setUser(buildImpersonatedUser(user, session.tenant, detail))
              setTenant(detail)
            } else {
              setUser(user)
            }
          }
        }
      } catch {
        // Invalid/expired token (or failed refresh) → drop the session so the
        // router sends the user to /login rather than a half-authenticated state.
        if (live.current) clearSession()
      } finally {
        if (live.current) setStatus('ready')
      }
    })()

    return () => {
      live.current = false
    }
  }, [task])

  return status
}
